import { Box, Typography } from '@mui/material'
import type { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { lastStep } from './common-steps'

export function constructDppPartnerTour({
  breakpoint,
  hasBasecampLink,
  hasKeyFigures,
  pool,
}: {
  breakpoint: Breakpoint
  hasBasecampLink: boolean
  hasKeyFigures: boolean
  pool: string
}): Step[] {
  const isSmallSizeTour = ['xs', 'sm', 'md'].includes(breakpoint)

  const steps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to InSite
          </Typography>
          <Typography variant="paragraph2" component="p">
            This tour introduces the key areas of InSite and shows you where to
            find your pool and vessel information.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Main navigation
          </Typography>
          <Typography variant="paragraph2" component="p">
            {isSmallSizeTour
              ? 'Open the menu at the top to find Dashboard, Financials, Emissions, Fleet and Operations. Select a section to see its available pages.'
              : 'The menu on the left takes you to the main areas of InSite, including Dashboard, Financials, Emissions, Fleet and Operations. Select a section to open its pages, and use the arrow at the top to open or close the menu.'}
          </Typography>
          {!isSmallSizeTour && (
            <Box
              component="img"
              src="/navigation-open-intro.gif"
              alt="Recording showing how to open and close the navigation menu."
              sx={{ display: 'block', mt: 2, width: '100%', borderRadius: 1 }}
            />
          )}
        </>
      ),
      target: isSmallSizeTour
        ? '.tour-step-nav-hamburger'
        : '.tour-step-nav-pane',
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Pool selector
          </Typography>
          <Typography variant="paragraph2" component="p">
            Switch between your pools here. You might see one or more pools
            depending on your vessel with Maersk Tankers.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            The Dashboard updates to show data only for the selected pool. This
            applies to all pages.
          </Typography>
        </>
      ),
      target: '.tour-step-pool-selector',
      placement: 'bottom',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Dashboard
          </Typography>
          <Typography variant="paragraph2" component="p">
            Use the Dashboard as your starting point.
          </Typography>
          {hasKeyFigures && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Key Figures gives you a snapshot of pool earnings and forecast.
            </Typography>
          )}
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Updates keeps you informed of the latest market comments, news and
            cargo activity.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Documents gives you quick access to shared reports and files.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can watch a dedicated tour for this section. Start it from the
            Help page.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="/dashboard/${pool}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
      skipBeacon: true,
      data: { navigationLink: `/dashboard/${pool}` },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Historical data
          </Typography>
          <Typography variant="paragraph2" component="p">
            Older reports and data from before InSite are still available in
            Basecamp.
            {hasBasecampLink && ' This link opens it in a new tab.'}
          </Typography>
        </>
      ),
      target: hasBasecampLink ? '.tour-step-dashboard-basecamp-link' : 'body',
      placement: hasBasecampLink ? 'top' : 'center',
      data: { next: `/financials/earnings/${pool}` },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Earnings
          </Typography>
          <Typography variant="paragraph2" component="p">
            Find earnings for your selected pool here. Choose a period to
            explore the results, or click on a vessel to see its financial
            information.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can watch a dedicated tour for this section. Start it from the
            Help page.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
      data: {
        previous: `/dashboard/${pool}`,
        next: `/financials/forecast/${pool}`,
        navigationLink: `/financials/earnings/${pool}`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Forecast
          </Typography>
          <Typography variant="paragraph2" component="p">
            Forecast shows a fixed and unfixed outlook for your selected pool.
            You may know this information as Trends: the quality of data is the
            same, just under a different name.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
      data: {
        previous: `/financials/earnings/${pool}`,
        next: '/fleet/vessels?track=pool',
        navigationLink: `/financials/forecast/${pool}`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Fleet
          </Typography>
          <Typography variant="paragraph2" component="p">
            Find the vessels you have access to here. Click on a vessel to
            explore its details.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can watch a dedicated tour for this section. Start it from the
            Help page.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
      data: {
        previous: `/financials/forecast/${pool}`,
        next: `/documents/${pool}`,
        navigationLink: '/fleet/vessels',
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Documents
          </Typography>
          <Typography variant="paragraph2" component="p">
            Find shared reports and files here, or access them from the
            Dashboard. Search for what you need and bookmark important documents
            to find them again easily.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            This is also where you'll find your TC hire statements
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
      data: {
        previous: '/fleet/vessels?track=pool',
        next: '/profile',
        navigationLink: `/documents/${pool}`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Date format
          </Typography>
          <Typography variant="paragraph2" component="p">
            Choose your preferred date format{' '}
            {isSmallSizeTour ? 'on your Profile page' : 'here'}. Your selection
            is used throughout InSite.
          </Typography>
        </>
      ),
      target: '.tour-step-profile-date-format',
      placement: isSmallSizeTour ? 'center' : 'left',
      data: { previous: `/documents/${pool}` },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Email notifications
          </Typography>
          <Typography variant="paragraph2" component="p">
            Manage your email subscriptions from your Profile
            {isSmallSizeTour && ' page too'}. Choose which pools and which
            topics you want to receive email notifications about.
          </Typography>
        </>
      ),
      target: '.tour-step-profile-subscriptions',
      placement: isSmallSizeTour ? 'center' : 'left',
    },
    {
      ...lastStep,
      content: (
        <>
          {lastStep.content}
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Would you like a deeper dive into the Dashboard?
          </Typography>
        </>
      ),
      data: {
        startTour: {
          id: '6',
          link: `/dashboard/${pool}`,
          label: 'Start Dashboard tour',
        },
      },
    },
  ]

  return steps.map((step) => ({
    ...step,
    skipBeacon: true,
    ...(step.data?.navigationLink
      ? {
          target: `.tour-step-nav-pane a[href="${step.data.navigationLink}"]`,
          placement: isSmallSizeTour ? ('bottom' as const) : ('right' as const),
          skipBeacon: true,
          targetWaitTimeout: 15000,
        }
      : {}),
    data: { ...step.data, deferredTarget: true },
  }))
}
